import React, { useContext, useEffect, useState } from "react";
import axios from "axios";
import { CircularProgress, IconButton, TextField } from "@mui/material";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import FilterListIcon from "@mui/icons-material/FilterList";
import ErrorIcon from "@mui/icons-material/Error";
import dayjs from "dayjs";
import { ToastContainer, Slide } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import "./PullListWorkflowStyles.css";
import Header from "../../components/Header";
import PullListWorkflowFilters from "../../components/PullListWorkflowFilters/PullListWorkflowFilters";
import PullListWorkflowDateFilters from "../../components/PullListWorkflowFilters/PullListWorkflowDateFilters";
import CustomDatePicker from "../../components/PullListWorkflowFilters/CustomDatePicker";
import { isValidDate } from "../../util/FormUtils";
import { UserContext } from "../../context/UserContext";
import ROLES from "../../util/roles";
import { checkRole } from "../../util/FormUtils";
import BASE_URL from "../../config/apiConfig";

const PullListWorkflows = () => {
    const { user } = useContext(UserContext);
    const columns = [
        "EP",
        "Title",
        "Series",
        "Shipment from Repo",
        "Status",
        "Area",
        "Study Number",
        "Biologist",
        "Lab",
        "Assay Type",
        "Date Opened",
        "Last Updated",
        "Date EP Approved",
        "Date Pull List Approved",
        "Expected Data Transfer to ITN",
        "DCT Data Received",
        "DCT Data Validation Complete",
        "EP Update",
    ];
    const [height, setHeight] = useState(0);
    const [loading, setLoading] = useState(false);
    const [workflows, setWorkflows] = useState([]);
    const [dataDes, setDataDes] = useState([]);
    const [dataAsc, setDataAsc] = useState([]);
    const [sort, setSort] = useState("descending");
    const [searchTerms, setSearchTerms] = useState("");
    const [openFilterWindow, setOpenFilterWindow] = useState(false);
    const [openDateFilterWindow, setOpenDateFilterWindow] = useState(false);
    const [filters, setFilters] = useState({});
    const [dateFilters, setDateFilters] = useState({
        createdAt: { start: null, end: null },
        updatedAt: { start: null, end: null },
        epApprovalDate: { start: null, end: null },
        plApprovalDate: { start: null, end: null },
        expectedDataDate: { start: null, end: null },
        resultsReceivedDate: { start: null, end: null },
        resultsValidatedDate: { start: null, end: null },
    });
    const [editIndex, setEditIndex] = useState(null);
    const [editField, setEditField] = useState(null);
    const [editValue, setEditValue] = useState("");
    const [open, setOpen] = useState(false);

    useEffect(() => {
        document.title = "EP Workflows";
    }, []);

    useEffect(() => {
        const updateHeight = () => {
            const windowHeight = window.innerHeight;
            const sectionTop = document.querySelector(".workflowTableContainer").getBoundingClientRect().top;
            const remainingHeight = windowHeight - sectionTop;
            setHeight(remainingHeight);
        };

        updateHeight();
        window.addEventListener("resize", updateHeight);

        return () => {
            window.removeEventListener("resize", updateHeight);
        };
    }, []);

    const fetchData = async () => {
        const workflowsResponse = await axios.get(BASE_URL + "/api/workflows", { withCredentials: true });
        const wfs = workflowsResponse.data;

        await axios.get(BASE_URL + "/api/labs", { withCredentials: true }).then((response) => {
            let labsList = {};
            Object.values(response.data).forEach((lab) => {
                labsList[lab.Code] = lab.Name;
            });

            const wflows = wfs.map((workflow) => ({ ...workflow, labs: labsList[workflow.labs] || workflow.labs }));
            setWorkflows(wflows);
            setDataDes([...wflows.sort((a, b) => a.epId - b.epId)]);
            setDataAsc([...wflows.sort((a, b) => b.epId - a.epId)]);
        });
    }

    useEffect(() => {
        fetchData().then(() => {
            setLoading(false);
        });
    }, []);

    useEffect(() => {
        if (sort === "descending") {
            setWorkflows(dataDes
                .filter((wflows) => containSearchTerms(wflows, searchTerms.toLowerCase()))
                .filter((wflows) => matchFilters(wflows))
                .filter((wflows) => matchDateFilters(wflows))
                .sort((a, b) => b.epId - a.epId)
            );
        } else {
            setWorkflows(dataAsc
                .filter((wflows) => containSearchTerms(wflows, searchTerms.toLowerCase()))
                .filter((wflows) => matchFilters(wflows))
                .filter((wflows) => matchDateFilters(wflows))
                .sort((a, b) => a.epId - b.epId)
            );
        }
    }, [searchTerms, filters, dateFilters]);

    const sortOrder = () => {
        if (sort === "descending") {
            setSort("ascending");
            const newData = workflows.sort((a, b) => a.epId - b.epId);
            setWorkflows(newData);
        } else {
            setSort("descending");
            const newData = workflows.sort((a, b) => b.epId - a.epId);
            setWorkflows(newData);
        }
    }

    const containSearchTerms = (wflows, searchTerms) => {
        return (
            wflows.epId.toString()?.toLowerCase().includes(searchTerms) ||
            wflows.title?.toLowerCase().includes(searchTerms)
        );
    }

    const handleSearch = (e) => {
        setSearchTerms(e.target.value);
    }

    const handleSearchClear = () => {
        setSearchTerms("");
    }

    const matchFilters = (wflows) => {
        for (const fieldName in filters) {
            const selectedFilter = filters[fieldName];

            if (!selectedFilter?.length) {
                continue;
            }

            if (!selectedFilter.some(fieldType => wflows[fieldName]?.includes(fieldType))) {
                return false;
            }
        }
        return true;
    };

    const matchDateFilters = (workflow) => {
        for (const fieldName in dateFilters) {
            const filter = dateFilters[fieldName];
            const fieldDateString = workflow[fieldName];

            // If field has no valid filter, skip
            if (!isValidDate(filter.start) && !isValidDate(filter.end)) {
                continue;
            }

            if (isValidDate(fieldDateString)) {
                const fieldDate = new Date(fieldDateString);

                if ((filter.start && fieldDate < new Date(filter.start))
                    || (filter.end && fieldDate > new Date(filter.end))) {
                    return false;
                }
            } else {
                return false;
            }
        }

        return true;
    };

    const handleChange = async (index, field, value) => {
        const newWorkflows = [...workflows];
        newWorkflows[index] = { ...newWorkflows[index], [field]: value };
        setWorkflows(newWorkflows);
    }

    const handleEditClick = (index, field, value) => {
        setEditIndex(index);
        setEditField(field);
        setEditValue(value);
        setOpen(true);
    };

    const handleBlur = async (value) => {
        if (value && value !== editValue &&
            (!editField.includes("Date") || dayjs(value, "YYYY-MM-DD", true).isValid())) {
            try {
                const response = await axios.get(`${BASE_URL}/api/pullLists/ep/${workflows[editIndex].epId}/pl/${workflows[editIndex].pullListId}/latest`, { withCredentials: true });
                const newData = {
                    ...response.data,
                    updatedAt: new Date().toISOString(),
                    updatedBy: user.name,
                    createdAt: null,
                    version: 1 + response.data.version,
                    [editField]: value
                };
                await axios.post(`${BASE_URL}/api/pullLists`, newData, { withCredentials: true });
            } catch (error) {
                console.error("Error while handling change:", error);
            }
        }

        const updatedWorkflow = { ...workflows[editIndex] };
        const updatedDataAsc = dataAsc.map(workflow => {
            if (workflow.epId === updatedWorkflow.epId && workflow.pullListId === updatedWorkflow.pullListId) {
                return { ...workflow, [editField]: value };
            }
            return workflow;
        });
        const updatedDataDes = dataDes.map(workflow => {
            if (workflow.epId === updatedWorkflow.epId && workflow.pullListId === updatedWorkflow.pullListId) {
                return { ...workflow, [editField]: value };
            }
            return workflow;
        });

        setDataAsc(updatedDataAsc);
        setDataDes(updatedDataDes);
        setEditIndex(null);
        setEditField(null);
        setEditValue(null);
        setOpen(false);
    };

    return (
        <div
            onClick={() => {
                setOpenFilterWindow(false);
                setOpenDateFilterWindow(false);
            }}
        >
            <Header/>
            {loading ? (
                <div className="loader center">
                    <i className="fa fa-cog fa-spin"/>
                </div>
            ) : (
                <>
                    <main className="workflowContainer">
                        <div className="workflowActions">
                            <div className="workflowSearch">
                                <TextField
                                    className="workflowSearchField"
                                    placeholder="Plan ID or Title"
                                    value={searchTerms}
                                    onChange={handleSearch}
                                    InputProps={{
                                        startAdornment: <SearchIcon className="workflowSearchIcon" fontSize="small"/>,
                                        endAdornment: searchTerms.length ? (
                                            <IconButton onClick={() => handleSearchClear()}>
                                                <ClearIcon className="workflowClearIcon" fontSize="small"/>
                                            </IconButton>
                                        ) : null,
                                    }}
                                    sx={{ fontSize: "15px", height: "48px", width: "576px", padding: 0, margin: 0 }}
                                />
                            </div>
                            <div className="workflowSortFilterAdd">
                                <a
                                    className="workflowFilter"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setOpenFilterWindow(false);
                                        setOpenDateFilterWindow((prev) => !prev);
                                    }}
                                    style={{ backgroundColor: Object.values(dateFilters).some(filter => filter.start || filter.end) ? "#cceeff" : "inherit" }}
                                >
                                    <span className="workflowActionIcon">
                                        <FilterListIcon/>
                                    </span>
                                    <span className="workflowActionName">Date Filters</span>
                                </a>
                                {openDateFilterWindow && (
                                    <PullListWorkflowDateFilters
                                        dateFilters={dateFilters}
                                        setDateFilters={setDateFilters}
                                    />
                                )}
                            </div>
                            <div className="workflowSortFilterAdd">
                                <a
                                    className="workflowFilter"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setOpenDateFilterWindow(false);
                                        setOpenFilterWindow((prev) => !prev);
                                    }}
                                    style={{ backgroundColor: Object.values(filters).some(value => !!value && value.length > 0) ? "#cceeff" : "inherit" }}
                                >
                                    <span className="workflowActionIcon">
                                        <FilterListIcon/>
                                    </span>
                                    <span className="workflowActionName">Filters</span>
                                </a>
                                {openFilterWindow && (
                                    <PullListWorkflowFilters
                                        filters={filters}
                                        setFilters={setFilters}
                                        workflows={dataDes}
                                    />
                                )}
                            </div>
                        </div>
                        <div className="workflowTableContainer" style={{ height: `${height}px` }}>
                            <div className="workflowTable">
                                <div className="workflowTableHeader">
                                    {columns.map((column, index) => (
                                        <div key={index} className="workflowTableFieldTitle">
                                            <span>{column}</span>
                                            {column === "EP" && (
                                                <button className="sortButton" onClick={sortOrder}>
                                                    {sort === "descending" ? (
                                                        <ArrowDownwardIcon fontSize="small"/>
                                                    ) : (
                                                        <ArrowUpwardIcon fontSize="small"/>
                                                    )}
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                                {workflows.length ? (
                                    <div className="workflowTableBody">
                                        {workflows.map((workflow, index) => {
                                            if (workflow.epId === 0 && !checkRole(user, [ROLES.ADMIN])) {
                                                return null;
                                            }

                                            return (
                                                <div className="workflowTableBodyRow" key={index}>
                                                    <a href={`/xplans/${workflow.epId}`} className="workflowTableLink"
                                                       target="_blank" rel="noopener noreferrer">
                                                        <div className="workflowTableId">{workflow.epId}</div>
                                                    </a>
                                                    <a href={`/xplans/${workflow.epId}`} className="workflowTableLink"
                                                       target="_blank" rel="noopener noreferrer">
                                                        <div className="workflowTableTitle">{workflow.title}</div>
                                                    </a>
                                                    <div className="workflowTableWorkflowId">{workflow.pullListId}</div>
                                                    <div className="workflowTableShipment">{workflow.shipmentFromRepo}</div>
                                                    <div className="workflowTableStatus">{workflow.plStatus}</div>
                                                    <div className="workflowTableArea">
                                                        {workflow.area ? workflow.area.replaceAll(",", ", ") : workflow.area}
                                                    </div>
                                                    <div className="workflowTableStudyNumber">
                                                        {workflow.protocol ? workflow.protocol.replaceAll(",", ", ") : workflow.protocol}
                                                    </div>
                                                    <div className="workflowTableBiologist">
                                                        {workflow.managedBy ? workflow.managedBy.replaceAll(",", ", ") : workflow.managedBy}
                                                    </div>
                                                    <div className="workflowTableLab">{workflow.labs}</div>
                                                    <div className="workflowTableAssayType">
                                                        {workflow.assayType ? workflow.assayType.replaceAll(",", ", ") : workflow.assayType}
                                                    </div>
                                                    <div className="workflowTableDateOpened">
                                                        {workflow.createdAt ? workflow.createdAt.slice(0, 10) : workflow.createdAt}
                                                    </div>
                                                    <div className="workflowTableDateUpdated">
                                                        {workflow.updatedAt ? workflow.updatedAt.slice(0, 10) : workflow.updatedAt}
                                                    </div>
                                                    <div className="workflowTableDateApprovedEP">{workflow.epApprovalDate}</div>
                                                    <div className="workflowTableDateApprovedPL">{workflow.plApprovalDate}</div>
                                                    <div className="workflowTableDataCell"
                                                         onClick={() => handleEditClick(index, "expectedDataDate", workflow.expectedDataDate)}
                                                    >
                                                        <span className="workflowTableDateExpectedData">
                                                            {workflow.expectedDataDate}
                                                        </span>
                                                    </div>
                                                    {editIndex === index && editField === "expectedDataDate" && open === true &&
                                                        <CustomDatePicker
                                                            open={editIndex === index && editField === "expectedDataDate" && open === true}
                                                            date={workflow.expectedDataDate}
                                                            index={index}
                                                            field={"expectedDataDate"}
                                                            handleChange={handleChange}
                                                            handleBlur={handleBlur}
                                                        />
                                                    }
                                                    <div className="workflowTableDataCell"
                                                         onClick={() => handleEditClick(index, "resultsReceivedDate", workflow.resultsReceivedDate)}
                                                    >
                                                        <span className="workflowTableDateDataReceived">
                                                            {workflow.resultsReceivedDate}
                                                        </span>
                                                    </div>
                                                    {editIndex === index && editField === "resultsReceivedDate" && open === true &&
                                                        <CustomDatePicker
                                                            open={editIndex === index && editField === "resultsReceivedDate" && open === true}
                                                            date={workflow.resultsReceivedDate}
                                                            index={index}
                                                            field={"resultsReceivedDate"}
                                                            handleChange={handleChange}
                                                            handleBlur={handleBlur}
                                                        />
                                                    }
                                                    <div className="workflowTableDataCell"
                                                         onClick={() => handleEditClick(index, "resultsValidatedDate", workflow.resultsValidatedDate)}
                                                    >
                                                        <span className="workflowTableDateValidationComplete">
                                                            {workflow.resultsValidatedDate}
                                                        </span>
                                                    </div>
                                                    {editIndex === index && editField === "resultsValidatedDate" && open === true &&
                                                        <CustomDatePicker
                                                            open={editIndex === index && editField === "resultsValidatedDate" && open === true}
                                                            date={workflow.resultsValidatedDate}
                                                            index={index}
                                                            field={"resultsValidatedDate"}
                                                            handleChange={handleChange}
                                                            handleBlur={handleBlur}
                                                        />
                                                    }
                                                    {editIndex === index && editField === "epUpdate" ? (
                                                        <div className="workflowTableEPUpdateTextBox">
                                                            <TextField
                                                                type="text"
                                                                value={workflow.epUpdate ? workflow.epUpdate : ""}
                                                                inputRef={(input) => input && input.focus()}
                                                                onChange={(e) => handleChange(index, "epUpdate", e.target.value)}
                                                                onBlur={() => handleBlur(workflow.epUpdate)}
                                                                onFocus={(e) => {
                                                                    const val = e.target.value;
                                                                    e.target.value = "";
                                                                    e.target.value = val;
                                                                }}
                                                                multiline
                                                                minRows={1}
                                                                sx={{ width: "360px" }}
                                                            />
                                                        </div>
                                                    ) : (
                                                        <div className="workflowTableDataCell"
                                                             onClick={() => handleEditClick(index, "epUpdate", workflow.epUpdate)}
                                                        >
                                                            <span className="workflowTableEPUpdate">
                                                                {workflow.epUpdate}
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <>
                                        {Object.keys(filters).every(key => !filters[key]) &&
                                        Object.values(dateFilters).every(filter => filter.start === null && filter.end === null) ? (
                                            <div className="workflowTableNotFound">
                                                <CircularProgress
                                                    size={16}
                                                    style={{ color: "#3c3f50", marginRight: "2px" }}
                                                />
                                                <p>Loading pull lists...</p>
                                            </div>
                                        ) : (
                                            <div className="workflowTableNotFound">
                                                <ErrorIcon fontSize="small"/>
                                                <p>No pull lists found.</p>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </main>
                </>
            )}
            <ToastContainer
                position="top-center"
                autoClose={3000}
                hideProgressBar
                newestOnTop
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
                transition={Slide}
            />
        </div>
    );
}

export default PullListWorkflows;